window.optionLengthLastClickedValue = window.optionLengthLastClickedValue || null;
window.optionLengthPendingSliderValue = window.optionLengthPendingSliderValue ?? null;
window.optionLengthPendingSliderLeft = window.optionLengthPendingSliderLeft ?? null;

class OptionLengthRange extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.selectors = {
      slider: '[data-option-range-slider]',
      selectedValue: '[data-selected-value]',
      exactInput: '[data-exact-dimension-input]',
      mainLengthSelector: '[data-select-label="Size"][data-option-position="2"],[data-select-label="Size"],[data-select-label="size"],[data-select-label="Length"]',
      container: '.length-range',
      knob: '[data-knob-value]',
    };
    this.init();
  }

  init() {
    this.slider = this.querySelector(this.selectors.slider);
    this.selectedValueDisplay = this.querySelector(this.selectors.selectedValue);
    this.exactInput = this.querySelector(this.selectors.exactInput);
    this.container = this.querySelector(this.selectors.container);
    this.knob = this.querySelector(this.selectors.knob);

    if (!this.container || !this.slider) return;

    this.optionValues = JSON.parse(this.container.dataset.optionValues || '[]');
    this.numericOptions = this.optionValues.map(v => parseFloat(v));

    this.slider.min = Math.min(...this.numericOptions);
    this.slider.max = Math.max(...this.numericOptions);
    this.slider.step = 1;

    if (this.exactInput) {
      this.exactInput.removeAttribute('min');
      this.exactInput.removeAttribute('max');
      this.exactInput.addEventListener('input', this.handleExactInputChange.bind(this));
      this.exactInput.addEventListener('blur', this.handleExactInputBlur.bind(this));
    }

    // Find or create the product form and hidden input for line item properties
    this.setupLineItemProperty();

    // Apply stored slider value if it exists and is valid for this slider
    if (
      window.optionLengthPendingSliderValue !== null &&
      this.numericOptions.includes(Number(window.optionLengthPendingSliderValue))
    ) {
      this.slider.value = window.optionLengthPendingSliderValue;
      this.updateKnob(window.optionLengthPendingSliderValue);
      this.updateSelectedValueDisplay(window.optionLengthPendingSliderValue);
    } else {
      this.slider.value = parseFloat(this.slider.value) || this.slider.min;
      this.updateKnob(this.slider.value);
      this.updateSelectedValueDisplay(this.slider.value);
    }

    this.slider.addEventListener('input', this.handleSliderChange.bind(this));
    this.handleSliderChange({ target: this.slider });
  }

  setupLineItemProperty() {
    const productForm = this.closest('.product-section')?.querySelector('form[action^="/cart/add"][data-product-form]');
    if (!productForm) return;

    // Check if hidden input already exists
    this.lineItemInput = productForm.querySelector('input[name="properties[Customer Selected Length]"]');

    // Create hidden input if it doesn't exist
    if (!this.lineItemInput) {
      this.lineItemInput = document.createElement('input');
      this.lineItemInput.type = 'hidden';
      this.lineItemInput.name = 'properties[Customer Selected Length]';
      productForm.appendChild(this.lineItemInput);
    }
  }

  updateLineItemProperty(value) {
    if (!this.lineItemInput) {
      console.warn('lineItemInput not found, cannot update');
      return;
    }

    const numericValue = parseFloat(value);
    const { feet, inches } = this.convertCmToFt(numericValue);

    let ftDisplay = '';
    if (feet > 0 && inches > 0) {
      ftDisplay = `${feet}ft ${inches}in`;
    } else if (feet > 0) {
      ftDisplay = `${feet}ft`;
    } else {
      ftDisplay = `${inches}in`;
    }

    const newValue = `${numericValue}cm (approx ${ftDisplay})`;
    this.lineItemInput.value = newValue;
  }

  convertCmToFt(cm) {
    const inches = cm / 2.54;
    const feet = Math.floor(inches / 12);
    const remainingInches = Math.round(inches % 12);
    return { feet, inches: remainingInches };
  }

  updateSelectedValueDisplay(value) {
    if (!this.selectedValueDisplay) return;

    const numericValue = parseFloat(value);
    const { feet, inches } = this.convertCmToFt(numericValue);

    let ftDisplay = '';
    if (feet > 0 && inches > 0) {
      ftDisplay = `${feet}ft ${inches}in`;
    } else if (feet > 0) {
      ftDisplay = `${feet}ft`;
    } else {
      ftDisplay = `${inches}in`;
    }

    // If exactInput is present, only show the approx part in the span to avoid redundancy
    if (this.exactInput) {
      this.selectedValueDisplay.textContent = `(approx ${ftDisplay})`;
    } else {
      this.selectedValueDisplay.textContent = `${numericValue}cm (approx ${ftDisplay})`;
    }
  }

  updateKnob(value) {
    const min = Number(this.slider.min);
    const max = Number(this.slider.max);
    const percent = ((value - min) / (max - min)) * 100;

    this.knob.style.left = `${percent}%`;
    this.knob.textContent = `${value} cm`;

    // Always save current knob left position globally
    window.optionLengthPendingSliderLeft = `${percent}%`;
  }

  debounce(func, delay = 300) {
    clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(func, delay);
  }

  handleExactInputChange(event) {
    let val = parseFloat(event.target.value);
    const min = parseFloat(this.slider.min);
    const max = parseFloat(this.slider.max);

    if (isNaN(val)) return;
    
    // Only update slider if within bounds, otherwise let user keep typing
    if (val >= min && val <= max) {
      this.slider.value = val;
      this.handleSliderChange({ target: this.slider });
    }
  }

  handleExactInputBlur(event) {
    let val = parseFloat(event.target.value);
    const min = parseFloat(this.slider.min);
    const max = parseFloat(this.slider.max);

    if (isNaN(val)) {
      val = min;
    } else if (val < min) {
      val = min;
    } else if (val > max) {
      val = max;
    }

    event.target.value = val;
    this.slider.value = val;
    this.handleSliderChange({ target: this.slider });
  }

  handleSliderChange(event) {
    const val = parseFloat(event.target.value);
    
    if (this.exactInput && parseFloat(this.exactInput.value) !== val) {
      this.exactInput.value = val;
    }

    const values = this.optionValues;
    const nums = values.map(v => parseFloat(v));

    // Choose the smallest available option >= slider value (round up).
    // If none are >= value, choose the largest available option.
    const sorted = nums.map((n, i) => ({ n, i })).sort((a, b) => a.n - b.n);
    const found = sorted.find(item => item.n >= val);
    const chosen = found ? found : sorted[sorted.length - 1];
    const nearestFull = values[chosen.i];

    this.updateKnob(val);

    this.updateSelectedValueDisplay(val);

    this.updateLineItemProperty(val);

    const tooltipElement = this.container.querySelector('[data-tippy-content]');
    if (tooltipElement) {
      const newTooltipText = `You selected: ${val} cm. Nearest available option: ${nearestFull}.`;
      tooltipElement.setAttribute('data-tippy-content', newTooltipText);
      if (tooltipElement._tippy) {
        tooltipElement._tippy.setContent(newTooltipText);
      }
    }

    if (nearestFull !== window.optionLengthLastClickedValue) {
      this.debounce(() => {
        const mainLengthSelector = document.querySelector(this.selectors.mainLengthSelector);
        const selectItem = mainLengthSelector?.querySelector(`a[data-value="${nearestFull}"]`);
        if (selectItem) {
          selectItem.click();
          window.optionLengthLastClickedValue = nearestFull;

          // Update global pending value **without expiration**
          window.optionLengthPendingSliderValue = nearestFull;
        }
      }, 300);
    }

    const nearestIndex = values.indexOf(nearestFull);
    this.dispatchEvent(new CustomEvent('length-changed', {
      detail: { value: val, index: nearestIndex },
      bubbles: true
    }));
  }
}

customElements.define('option-length-range', OptionLengthRange);
